import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import { toast } from 'sonner';
import { Calendar, CheckCircle, Users } from 'lucide-react';
import { sortPrograms } from '../../utils/programSort';
import { useStructuredData, getCourseSchema } from '../../components/StructuredData';
import { useTenant } from '../../context/TenantContext';
import DescriptionView from '../../components/DescriptionView';

const ParentPrograms = () => {
  const { branding } = useTenant();
  const tenantName = branding?.business_name || 'Cricket Academy';
  const [programs, setPrograms] = useState([]);
  const [players, setPlayers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Enrollment Dialog
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');

  const fetchData = async () => {
    try {
      const [programsRes, playersRes, enrollmentsRes] = await Promise.all([
        api.get('/programs'),
        api.get('/players'),
        api.get('/enrollments/my-enrollments')
      ]);
      setPrograms(sortPrograms(programsRes.data.filter(p => p.is_active)));
      setPlayers(playersRes.data);
      setEnrollments(enrollmentsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Generate ItemList schema for SEO
  const programListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Cricket Training Programs",
    "description": `Professional cricket coaching programs at ${tenantName}`,
    "numberOfItems": programs.length,
    "itemListElement": programs.map((program, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": getCourseSchema(program, tenantName)
    }))
  };

  // Inject structured data
  useStructuredData(programs.length > 0 ? programListSchema : null);

  const handleEnrollRequest = async (e) => {
    e.preventDefault();
    if (!selectedPlayer || !selectedProgram) {
      toast.error('Please select a player and program');
      return;
    }
    try {
      await api.post('/enrollments', {
        player_id: selectedPlayer,
        program_id: selectedProgram
      });
      toast.success('Enrollment request submitted');
      setEnrollDialogOpen(false);
      setSelectedPlayer('');
      setSelectedProgram('');
      fetchData(); // Refresh to show updated enrollment status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit enrollment');
    }
  };

  // Get enrolled program IDs and children info
  const getEnrollmentInfo = (programId) => {
    const programEnrollments = enrollments.filter(e => 
      e.program_id === programId && 
      ['approved', 'current', 'active'].includes(e.status)
    );
    return {
      isEnrolled: programEnrollments.length > 0,
      enrolledChildren: programEnrollments.map(e => e.player_name),
      count: programEnrollments.length
    };
  };

  // Get billing frequency label
  const getBillingFrequencyLabel = (program) => {
    if (program.billing_type === 'one_time') return 'One-time';
    
    switch (program.billing_frequency) {
      case 'monthly': return 'Monthly';
      case 'quarterly': return 'Quarterly';
      case 'bi_annually': 
      case 'biannually': return 'Bi-annually';
      case 'annually': return 'Annually';
      case 'weekly': return 'Weekly';
      default: return program.billing_frequency || 'Recurring';
    }
  };

  // Filter out expired programs
  const activePrograms = programs.filter(program => {
    if (program.program_type === 'recurring' || !program.end_date) {
      return true;
    }
    const endDate = new Date(program.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate >= today;
  });

  // Separate enrolled and available programs
  const enrolledPrograms = activePrograms.filter(p => getEnrollmentInfo(p.id).isEnrolled);
  const availablePrograms = activePrograms.filter(p => !getEnrollmentInfo(p.id).isEnrolled);

  const renderProgramCard = (program, isEnrolled = false) => {
    const enrollmentInfo = getEnrollmentInfo(program.id);
    
    return (
      <Card 
        key={program.id} 
        className={`border hover:shadow-md transition-shadow ${
          isEnrolled ? 'border-green-200 bg-green-50/30' : 'border-slate-200'
        }`} 
        data-testid={`program-card-${program.id}`}
      >
        <CardContent className="p-4 lg:p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading text-xl font-bold text-slate-900">{program.name}</h3>
                {isEnrolled && (
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Enrolled
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 capitalize">
                {program.skill_level} • {program.age_group || 'All ages'}
              </p>
            </div>
            <div className="text-right">
              <span className="font-heading text-2xl font-bold text-blue-600">
                {cs()}{program.price}
              </span>
              <p className="text-xs text-slate-500">{getBillingFrequencyLabel(program)}</p>
            </div>
          </div>
          
          {isEnrolled && enrollmentInfo.enrolledChildren.length > 0 && (
            <div className="mb-4 p-2 bg-green-100 rounded-lg">
              <p className="text-sm text-green-700 flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span className="font-medium">Enrolled:</span> {enrollmentInfo.enrolledChildren.join(', ')}
              </p>
            </div>
          )}
          
          {program.description ? (
            <DescriptionView value={program.description} className="text-sm text-slate-600 mb-4" />
          ) : (
            <p className="text-slate-600 text-sm mb-4">No description</p>
          )}
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {program.duration_weeks} weeks
            </span>
            <span>{program.sessions_per_week}x/week</span>
          </div>
          
          {!isEnrolled && (
            <Button 
              className="w-full bg-slate-900 hover:bg-slate-800"
              onClick={() => {
                setSelectedProgram(program.id);
                setEnrollDialogOpen(true);
              }}
              data-testid={`enroll-btn-${program.id}`}
            >
              Request Enrollment
            </Button>
          )}
          
          {isEnrolled && (
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => {
                setSelectedProgram(program.id);
                setEnrollDialogOpen(true);
              }}
              data-testid={`enroll-another-btn-${program.id}`}
            >
              Enroll Another Child
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Programs">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Programs">
      <div className="space-y-6" data-testid="programs-page">
        <p className="text-slate-600">Browse available programs and enroll your children</p>
        
        {/* Enrolled Programs Section */}
        {enrolledPrograms.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Currently Enrolled
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {enrolledPrograms.map((program) => renderProgramCard(program, true))}
            </div>
          </div>
        )}
        
        {/* Available Programs Section */}
        {availablePrograms.length > 0 && (
          <div>
            {enrolledPrograms.length > 0 && (
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Available Programs
              </h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availablePrograms.map((program) => renderProgramCard(program, false))}
            </div>
          </div>
        )}
        
        {activePrograms.length === 0 && (
          <div className="p-12 text-center">
            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No active programs available at the moment</p>
          </div>
        )}

        {/* Enrollment Dialog */}
        <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Request Enrollment</DialogTitle>
            </DialogHeader>
            {(() => {
              const selectedProgramData = programs.find(p => p.id === selectedProgram);
              return selectedProgramData ? (
                <form onSubmit={handleEnrollRequest} className="space-y-4 mt-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900">{selectedProgramData.name}</p>
                    <p className="text-sm text-slate-500">
                      {cs()}{selectedProgramData.price} • {getBillingFrequencyLabel(selectedProgramData)} • {selectedProgramData.duration_weeks} weeks
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Select Child</Label>
                    <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
                      <SelectTrigger data-testid="enroll-player-select">
                        <SelectValue placeholder="Choose a child" />
                      </SelectTrigger>
                      <SelectContent>
                        {players.map((player) => (
                          <SelectItem key={player.id} value={player.id}>
                            {player.full_name || player.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {players.length === 0 && (
                      <p className="text-sm text-amber-600">
                        You need to add a child first before enrolling.
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setEnrollDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-blue-500 hover:bg-blue-600" 
                      data-testid="submit-enrollment-btn"
                      disabled={players.length === 0}
                    >
                      Submit Request
                    </Button>
                  </div>
                </form>
              ) : null;
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentPrograms;
